import React, { useContext } from 'react';
import { CountryCode } from '@dayetopia/types';
import { useLocation } from '@reach/router';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { Context } from '@components/Context';
import { NavContext } from '@components/navigation/Context';
import Badge from '@components/shared/Badge';
import Colors from '@components/shared/Colors';
import TextLinkButton from '@components/shared/TextLinkButton';
import useDesktop from '@components/shared/useDesktop';
import {
  CONTRACEPTION_SVG,
  DASHBOARD_SVG,
  ENDO_SVG,
  MICROBIOME_SCREENING_SVG,
  PRESCRIPTIONS_SVG,
  PURCHASES_SVG,
  REFERRALLS_SVG,
  SETTINGS_SVG,
  BREAKPOINT,
  referralAmount
} from '@constants';
import { PageOptions, CurrencyObj, NavStatus } from '@contracts';
import useTranslate from '@hooks/useTranslate';
import ArrowIcon from '@images/account/arrow-right.svg';
import { getPriceText } from '@utils/price';
import useLogoutHandle from '@utils/useLogoutHandle';
import useNavigate from '@utils/useNavigate';

const VMSNavItems = [
  {
    id: 'dashboard',
    icon: DASHBOARD_SVG,
    title: 'vms-nav.dashboard',
    changePageTo: `/dashboard/`
  },
  {
    id: 'screenings',
    icon: MICROBIOME_SCREENING_SVG,
    title: 'vms-nav.screenings',
    changePageTo: `/dashboard/${PageOptions.MicrobiomeScreening}/`
  },
  {
    id: 'period-pain',
    icon: ENDO_SVG,
    title: 'vms-nav.period-pain',
    changePageTo: `/dashboard/${PageOptions.PeriodPainReport}/`
  },
  {
    id: 'contraception',
    icon: CONTRACEPTION_SVG,
    title: 'vms-nav.contraception',
    changePageTo: `/dashboard/${PageOptions.Contraception}/`
  },
  {
    id: 'prescriptions',
    icon: PRESCRIPTIONS_SVG,
    title: 'vms-nav.prescriptions',
    changePageTo: `/dashboard/${PageOptions.Prescriptions}/`
  },
  {
    id: 'purchases',
    icon: PURCHASES_SVG,
    title: 'vms-nav.orders',
    changePageTo: `/dashboard/${PageOptions.Purchases}/`
  },
  {
    id: 'referrals',
    icon: REFERRALLS_SVG,
    title: 'vms-nav.referrals',
    changePageTo: `/dashboard/${PageOptions.Referrals}/`,
    labelText: (currency: CurrencyObj, country: CountryCode, t: (key: string) => string) =>
      `Earn ${getPriceText(referralAmount, country, currency, t).fullString}`
  },
  {
    id: 'settings',
    icon: SETTINGS_SVG,
    title: 'vms-nav.account-settings',
    changePageTo: `/dashboard/${PageOptions.AccountSettings}/`
  }
];

const VMSNav: React.FC = () => {
  const { state } = useContext(Context);
  const { navigateWrapper } = useNavigate();
  const handleLogout = useLogoutHandle({ navigateLink: '' });
  const { country, currency } = state;
  const { t } = useTranslate();
  const filteredNavItems = () => {
    if (country !== CountryCode.GB && country !== CountryCode.US && country !== CountryCode.IE) {
      return VMSNavItems.filter((item) => item.id !== 'period-pain');
    }
    return VMSNavItems;
  };

  const navItems = filteredNavItems();

  return (
    <NavWrapper>
      {navItems.map((item) => (
        <VMSNavItem
          key={item.id}
          icon={item.icon}
          title={t(item.title)}
          link={item.changePageTo}
          labelText={typeof item.labelText === 'function' ? item.labelText(currency, country, t) : item.labelText}
        />
      ))}
      <SubNavLinks>
        <TextLinkButton $color={Colors.navyBlue} onClick={() => navigateWrapper('/')}>
          Daye homepage
        </TextLinkButton>
        <TextLinkButton $color={Colors.navyBlue} onClick={handleLogout}>
          Log out
        </TextLinkButton>
      </SubNavLinks>
    </NavWrapper>
  );
};

const NavWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  position: sticky;
  align-self: flex-start;
  z-index: 2;
  top: 100px;
  left: 0;
  bottom: 0;
  padding: 0 0 100px;
  max-width: 216px;

  @media (max-height: 880px) {
    position: relative;
  }

  @media (max-width: 1160px) {
    position: relative;
  }

  @media (max-width: ${BREAKPOINT.laptop}px) {
    width: 100%;
    max-width: 100%;
    padding: 0;
    top: auto;
    left: auto;
    bottom: auto;
  }
`;

const SubNavLinks = styled.div`
  flex-direction: row;
  margin-top: 30px;
  padding: 0 30px;
  gap: 30px;
  display: none;

  @media (max-width: ${BREAKPOINT.laptop}px) {
    display: flex;
  }
`;

interface Props {
  icon?: string;
  title: string;
  link: string;
  labelText?: string;
}

const VMSNavItem: React.FC<Props> = ({ icon, title, link, labelText }) => {
  const location = useLocation();
  const { navigateWrapper } = useNavigate();
  const isMobile = !useDesktop(1024);
  const { navDispatch } = useContext(NavContext);

  const handleItemClick = () => {
    navDispatch({ type: 'setStatus', data: NavStatus.Default });
    navigateWrapper(link);
  };

  const isActive = () => {
    if (link.endsWith('/dashboard/')) {
      return location.pathname.endsWith(link) || location.pathname.includes('/health-profile/');
    }
    return location.pathname.includes(link);
  };

  const active = isActive();

  return (
    <Container onClick={handleItemClick} className={`${active ? 'active' : ''}`}>
      {icon && (
        <IconWrap>
          <img src={icon} alt={title} />
        </IconWrap>
      )}
      <TitleWrap>
        <p>{title}</p>
        {labelText && (
          <BadgeWrap>
            <Badge text={labelText}></Badge>
          </BadgeWrap>
        )}
      </TitleWrap>
      {isMobile && <StyledArrow $active={!!active} />}
    </Container>
  );
};

export default VMSNav;

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 25px 30px;
  width: 215px;
  transition: background 0.3s;
  cursor: pointer;
  position: relative;

  @media (max-width: ${BREAKPOINT.laptop}px) {
    padding: 20px 30px;
    width: 100%;
    border-bottom: 1px solid ${Colors.ecru};
  }

  &.active {
    opacity: 1;
    background: ${Colors.cream};

    p {
      opacity: 1;
    }
  }

  &:hover {
    opacity: 1;
    background: ${Colors.cream};

    p {
      opacity: 1;
      font-weight: 700;
    }
  }

  p {
    font-family: BrownStd;
    font-size: 14px;
    font-weight: 400;
    line-height: 150%;
    color: ${Colors.navyBlue};
    text-align: left;
    transition: opacity 0.3s ease;

    @media (max-width: ${BREAKPOINT.laptop}px) {
      font-size: 18px;
    }
  }
`;

const IconWrap = styled.div`
  display: flex;
  flex-shrink: 0;

  & img {
    width: 40px;
    height: 40px;

    @media (max-width: ${BREAKPOINT.laptop}px) {
      width: 30px;
      height: 30px;
    }
  }
`;

const TitleWrap = styled.div`
  display: flex;
  flex-direction: column;
  column-gap: 8px;

  @media (max-width: ${BREAKPOINT.laptop}px) {
    flex-direction: row;
  }
`;

const BadgeWrap = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  > span {
    padding: 5px 8px 3px;
  }
`;

const StyledArrow = styled(ArrowIcon)<{ $active: boolean }>`
  display: ${({ $active }) => ($active ? 'block' : 'none')};
  margin-right: 0;
  position: absolute;
  width: 12px;
  height: 12px;
  right: 30px;

  & > path {
    fill: ${Colors.navyBlue};
  }

  @media (max-width: ${BREAKPOINT.tablet}px) {
    display: block;
  }
`;
